import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { hitApi } from '../../database/services/ApiHelper';
import { showToast } from '.';
import { Star, X, AlertTriangle, ThumbsUp } from 'lucide-react';

// RateReviewSheet — bottom sheet for rating + reviewing a book.
//
// Talks to /api/d2p/books/[id]/review (Bearer auth, JSON body — the
// route handles both multipart and JSON). The route already has a
// "you can't review your own book" guard server-side.
//
// API contract:
//   POST /api/d2p/books/[id]/review
//   { rating, comment, recommendBook, containsSpoilers, tags: [] }
//   → { success, review, averageRating, totalReviews, uploadedImages }
//
// Caller passes `existing` when editing — the form pre-fills with the
// user's prior values, the action label flips to "Update review", and
// we show the existing star rating + comment.

const BRAND = '#5B47E0';
const BRAND_DARK = '#4A37CC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';
const STAR_YELLOW = '#FFB800';

interface ExistingReview {
  rating: number;
  comment?: string;
  recommendBook?: boolean;
  containsSpoilers?: boolean;
}

interface Props {
  open: boolean;
  bookId: string;
  bookTitle: string;
  existing?: ExistingReview | null;
  onClose: () => void;
  onSubmitted: (review: any) => void;
}

const RateReviewSheet: React.FC<Props> = observer(({
  open,
  bookId,
  bookTitle,
  existing,
  onClose,
  onSubmitted,
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [recommend, setRecommend] = useState(true);
  const [spoilers, setSpoilers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill when editing or when the sheet re-opens for a different book.
  useEffect(() => {
    if (!open) return;
    setRating(existing?.rating ?? 0);
    setComment(existing?.comment ?? '');
    setRecommend(existing?.recommendBook ?? true);
    setSpoilers(existing?.containsSpoilers ?? false);
    setHoverRating(0);
    setSubmitting(false);
  }, [open, existing]);

  // Esc to close + lock body scroll.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      showToast('Please pick a star rating', 'warning', { subtitle: '1-5 stars' });
      return;
    }
    if (!userStore.authToken) {
      showToast('You need to be logged in', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await hitApi({
        endpoint: `api/d2p/books/${bookId}/review`,
        method: 'POST',
        body: {
          rating,
          comment: comment.trim(),
          recommendBook: recommend,
          containsSpoilers: spoilers,
          tags: [],
        },
        token: userStore.authToken,
      });
      showToast(existing ? 'Review updated' : 'Review posted', 'success', { subtitle: bookTitle });
      onSubmitted(res?.review ?? null);
      onClose();
    } catch (e: any) {
      const message = e?.data?.error || e?.message || 'Please try again';
      showToast('Could not post review', 'error', { subtitle: message });
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;
  const ratingHint =
    displayRating === 0 ? 'Tap to rate'
    : displayRating === 1 ? 'Didn’t enjoy it'
    : displayRating === 2 ? 'Not for me'
    : displayRating === 3 ? 'It was OK'
    : displayRating === 4 ? 'Really liked it'
    : 'Loved it';

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(15, 23, 42, 0.45)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl p-5 pb-7 safe-area-bottom"
        style={{
          background: '#FFFFFF',
          boxShadow: '0 -20px 40px -10px rgba(15, 23, 42, 0.25)',
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
        role="dialog"
        aria-label="Rate and review"
      >
        {/* Drag handle */}
        <div
          className="mx-auto h-1 w-12 rounded-full mb-4"
          style={{ background: '#CBD5E1' }}
          aria-hidden="true"
        />

        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 min-w-0 pr-3">
            <h2
              className="text-[19px] font-bold tracking-tight"
              style={{ color: TEXT_PRIMARY }}
            >
              {existing ? 'Update your review' : 'Rate this book'}
            </h2>
            <p
              className="text-[13px] mt-1 truncate"
              style={{ color: TEXT_MUTED }}
            >
              {bookTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: SURFACE }}
          >
            <X className="w-[16px] h-[16px]" style={{ color: TEXT_PRIMARY }} />
          </button>
        </div>

        {/* Star picker */}
        <div className="flex flex-col items-center mt-5 mb-3">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = n <= displayRating;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  aria-label={`Rate ${n} star${n === 1 ? '' : 's'}`}
                  className="transition-transform active:scale-90"
                >
                  <Star
                    className="w-9 h-9"
                    style={{
                      color: filled ? STAR_YELLOW : '#CBD5E1',
                      fill: filled ? STAR_YELLOW : 'transparent',
                    }}
                    strokeWidth={2}
                  />
                </button>
              );
            })}
          </div>
          <p
            className="text-[13px] font-medium mt-3"
            style={{ color: displayRating > 0 ? TEXT_PRIMARY : TEXT_MUTED }}
          >
            {ratingHint}
          </p>
        </div>

        {/* Comment */}
        <div className="mt-5">
          <label
            htmlFor="review-comment"
            className="block text-[13px] font-semibold mb-2"
            style={{ color: TEXT_PRIMARY }}
          >
            Your thoughts <span style={{ color: TEXT_MUTED, fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            id="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 1000))}
            placeholder="What stood out? Plot, writing, pacing — anything goes."
            rows={4}
            className="w-full rounded-2xl p-3 text-[14.5px] leading-relaxed resize-none outline-none transition-colors"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              color: TEXT_PRIMARY,
            }}
          />
          <p
            className="text-[11.5px] mt-1.5 text-right"
            style={{ color: TEXT_MUTED }}
          >
            {comment.length} / 1000
          </p>
        </div>

        {/* Toggles */}
        <div className="mt-3 space-y-2">
          <ToggleRow
            icon={<ThumbsUp className="w-[16px] h-[16px]" style={{ color: BRAND }} />}
            label="Recommend this book"
            value={recommend}
            onChange={setRecommend}
          />
          <ToggleRow
            icon={<AlertTriangle className="w-[16px] h-[16px]" style={{ color: '#F59E0B' }} />}
            label="Contains spoilers"
            value={spoilers}
            onChange={setSpoilers}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || rating < 1}
          className="w-full h-12 mt-6 rounded-2xl text-white text-[15px] font-semibold transition-transform active:scale-[0.98] disabled:opacity-50"
          style={{
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
            boxShadow: '0 12px 28px -8px rgba(91,71,224,0.5)',
          }}
        >
          {submitting
            ? 'Posting…'
            : existing
              ? 'Update review'
              : 'Post review'}
        </button>
      </div>
    </div>
  );
});

function ToggleRow({
  icon,
  label,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className="flex items-center gap-3 p-3 rounded-2xl cursor-pointer transition-colors"
      style={{ background: '#FAFAFD', border: '1px solid #F1F5F9' }}
    >
      <span
        className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: '#FFFFFF' }}
      >
        {icon}
      </span>
      <span className="flex-1 text-[14px] font-medium" style={{ color: TEXT_PRIMARY }}>
        {label}
      </span>
      <span
        role="switch"
        aria-checked={value}
        className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
        style={{ background: value ? BRAND : '#CBD5E1' }}
      >
        <span
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
          style={{ left: value ? 22 : 2 }}
        />
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
    </label>
  );
}

export default RateReviewSheet;
